import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import LoginBcgImage from "../../assets/images/bg-login.png";
import BgForgotImage from "../../assets/images/forgot-bg.png";
import Welcome from "../../common/components/welcome/welcome";
import { NavLink, useSearchParams } from "react-router-dom";
import { routes } from "../../constants/routes";
import "../login/login.scss";
import "../change-password/change-password.scss";
import backIcon from "../../assets/icons/back.svg";
import * as resetPasswordAPI from "../../api/resetpassword";
import { useSetLoading } from "../../common/hooks/appearance";
import "./reset-password.scss";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { AlertComponent } from "../../common/components";
import { getCurrentUser, logout } from "../../common/routines/user-operations";

interface AlertMessage {
  open: boolean;
  message?: any;
  type?: "success" | "error" | "info" | "warning";
  onClose?: () => void;
}

const ResetPassword: React.FC = () => {
  const [isResetSuccess, setIsResetSuccess] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<any | null>(null);
  const [step, setStep] = useState<"invalid" | "success">("invalid");
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [alertMessage, setAlertMessage] = useState<AlertMessage>({
    open: false,
    message: "",
    type: undefined,
  });

  const user = getCurrentUser();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const initialValues = { newPassword: "", confirmNewPassword: "" };

  const handleValidationToken = useCallback(async (payload: any) => {
    setIsLoading(true);
    try {
      const response = await resetPasswordAPI?.validationResetPasswordToken({
        token: payload,
      });
      if (response.status === 200 && response.data.success) {
        setStep("success");
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      setIsResetSuccess(false);
      setStep("invalid");
      setMessage({ message: error?.response?.data?.message, types: "error" });
    }
  }, []);

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      handleValidationToken(tokenFromUrl);
      setToken(tokenFromUrl);
    }
  }, [searchParams, handleValidationToken]);

  const validationSchema = Yup.object({
    newPassword: Yup.string()
      .required("New password is required")
      .min(8, "Password must be at least 8 characters")
      .max(30, "Password length must be less than 15 characters")
      .matches(/[A-Z]/, "Must contain at least one uppercase letter")
      .matches(/[a-z]/, "Must contain at least one lowercase letter")
      .matches(/\d/, "Must contain at least one number")
      .matches(/[^a-zA-Z0-9]/, "Must contain at least one special character"),
    confirmNewPassword: Yup.string()
      .oneOf([Yup.ref("newPassword")], "Passwords must match")
      .required("Confirm password is required"),
  });

  const onSubmit = async (values: typeof initialValues) => {
    setIsLoading(true);
    try {
      const payload = {
        token: token,
        newPassword: values?.newPassword,
        confirmNewPassword: values?.confirmNewPassword,
      };
      const response = await resetPasswordAPI?.resetpassword(payload);
      if (response.status === 200 && response.data.success) {
        setMessage({ message: response?.data?.message, types: "success" });
        setIsResetSuccess(true);
        setAlertMessage({
          open: true,
          message: "Password changed successfully!",
          type: "success",
        });
        if (user) logout();
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      const errorCode = error?.response?.data?.errorCode;
      const errorMsg =
        error?.response?.data?.message || error?.response?.data?.error;

      if (errorCode === "INVALID_NEW_PASSWORD") {
        setAlertMessage({
          open: true,
          message: errorMsg,
          type: "error",
        });
      } else {
        setMessage({ message: errorMsg, types: "error" });
        setAlertMessage({
          open: true,
          message: errorMsg || "Something went wrong!",
          type: "error",
        });
      }
    }
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <div
      className="login-page-wrapper"
      style={{
        backgroundImage: `url(${BgForgotImage}), url(${LoginBcgImage})`,
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundPosition: "center bottom, center bottom",
        backgroundSize: "100% 40%, 100% 40%",
      }}
    >
      <div className="login-card-wrapper">
        <div>
          {message ? (
            <div className="success-message-wrapper">
              {isResetSuccess && message.types === "success" ? (
                <div>
                  <h2>Password reset is successful!</h2>
                  <p>Your password has been reset successfully.</p>
                </div>
              ) : (
                <>
                  <p>{message?.message}</p>
                </>
              )}
              <div className="back-icon-wraper">
                <NavLink to={routes.LOGIN}>
                  <img src={backIcon} alt="back" />
                  <span>Back to Login</span>
                </NavLink>
              </div>
            </div>
          ) : (
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {() => (
                <Form className="loginForm resetPasswordForm">
                  <h2>Reset Password</h2>
                  <div className="formBody">
                    <div className="field">
                      <Field
                        type={showPassword ? "text" : "password"}
                        name="newPassword"
                        placeholder="New Password *"
                        maxLength={30}
                      />
                      <span
                        className="visibility-icon"
                        onClick={togglePasswordVisibility}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </span>
                      <ErrorMessage
                        name="newPassword"
                        component="div"
                        className="errorMessage"
                      />
                    </div>
                    <div className="field">
                      <Field
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmNewPassword"
                        placeholder="Confirm Password *"
                        maxLength={30}
                      />
                      <span
                        className="visibility-icon"
                        onClick={toggleConfirmPasswordVisibility}
                      >
                        {showConfirmPassword ? (
                          <Visibility />
                        ) : (
                          <VisibilityOff />
                        )}
                      </span>
                      <ErrorMessage
                        name="confirmNewPassword"
                        component="div"
                        className="errorMessage"
                      />
                    </div>

                    <button type="submit" className="submitButton">
                      Reset Password
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>
        <div className="right-content-wraper">
          <Welcome />
        </div>
      </div>
      {alertMessage.open && (
        <AlertComponent
          message={alertMessage.message}
          type={alertMessage.type || "info"}
          open={alertMessage.open}
          onClose={() => setAlertMessage({ open: false })}
        />
      )}
    </div>
  );
};

export default ResetPassword;
